import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, Loader2, Repeat, XCircle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TooltipButton } from "@/components/common/Tooltips";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/common/PageHeader";
import { LeadForm } from "@/components/common/LeadForm";
import { BotonAbrirEnMapas } from "@/components/common/BotonAbrirEnMapas";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { puedeProcesarLeads } from "@/lib/rbac";

export default function LeadsCampo() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canProcess = puedeProcesarLeads(user);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [descartar, setDescartar] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [capturar, setCapturar] = useState(false);

  const cargar = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data.leads || []);
    } catch (e) { console.debug("cargar leads (campo):", e?.response?.status); } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const convertir = async (lead) => {
    setBusy(lead.id);
    try {
      const { data } = await api.post(`/leads/${lead.id}/convertir`, {});
      toast.success("Lead convertido a prospecto. Completa el expediente KYC.");
      cargar();
      // Llevar a la gestión de prospectos para completar KYC.
      nav("/r/prospectos");
      return data;
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(null); }
  };

  const confirmarDescarte = async () => {
    if (!descartar) return;
    setBusy(descartar.id);
    try {
      await api.post(`/leads/${descartar.id}/descartar`, { motivo });
      toast.success("Lead descartado.");
      setDescartar(null); setMotivo("");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="leads-campo-page">
      <PageHeader title="Leads de campo" subtitle="Bandeja de prospectos capturados por vendedores · procesa y convierte" />
      <div className="p-6 md:p-8">
        <div className="mb-4 flex justify-end">
          <Button data-testid="capturar-lead-btn" onClick={() => setCapturar(true)} className="gap-2 bg-primary hover:bg-sidebar-hover">
            <Plus className="h-4 w-4" /> Capturar lead
          </Button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
            <Inbox className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-base font-semibold text-slate-800">Leads por procesar ({leads.length})</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
          ) : leads.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">No hay leads pendientes de procesar.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-2.5">Prospecto</th>
                  <th className="px-3 py-2.5">Dirección</th>
                  <th className="px-3 py-2.5">Monto / Tasa</th>
                  <th className="px-3 py-2.5">Vendedor</th>
                  <th className="px-3 py-2.5">Estado</th>
                  <th className="px-5 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-5 py-3 font-medium text-slate-700">{l.nombre}
                      {l.observacion && <p className="text-xs font-normal text-slate-400">{l.observacion}</p>}</td>
                    <td className="px-3 py-3 text-slate-600">
                      {l.direccion || "—"}
                      {l.geo_lead?.lat != null && (
                        <div className="mt-1">
                          <BotonAbrirEnMapas lat={l.geo_lead.lat} lng={l.geo_lead.lng}
                            className="h-7 px-2 text-[11px]" testid={`lead-mapa-${l.id}`} />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      {l.monto_solicitado != null ? `$${Number(l.monto_solicitado).toLocaleString("es-MX")}` : "—"}
                      {l.tasa_ofrecida ? ` · ${l.tasa_ofrecida}%` : ""}</td>
                    <td className="px-3 py-3 text-slate-500">{l.vendedor_nombre || "—"}</td>
                    <td className="px-3 py-3"><span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">{l.estado}</span></td>
                    <td className="px-5 py-3">
                      {canProcess ? (
                        <div className="flex justify-end gap-2" data-testid={`lead-acciones-${l.id}`}>
                          <TooltipButton tooltip="Convierte este lead en prospecto para iniciar el expediente"
                            data-testid={`convertir-lead-${l.id}`} size="sm" disabled={busy === l.id}
                            onClick={() => convertir(l)} className="gap-1.5 bg-accent hover:bg-forest-700">
                            {busy === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />} Procesar / convertir
                          </TooltipButton>
                          <TooltipButton tooltip="Descartar este lead (queda registrado en bitácora)"
                            data-testid={`descartar-lead-${l.id}`} size="sm" variant="outline"
                            onClick={() => setDescartar(l)} className="gap-1.5 border-red-200 text-red-700 hover:bg-red-50">
                            <XCircle className="h-4 w-4" /> Descartar
                          </TooltipButton>
                        </div>
                      ) : (
                        <div className="flex justify-end" data-testid={`lead-solo-captura-${l.id}`}>
                          <span className="text-xs text-slate-400">Solo captura</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={!!descartar} onOpenChange={(o) => !o && setDescartar(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Descartar lead — {descartar?.nombre}</DialogTitle>
            <DialogDescription>Indica el motivo. Queda registrado en bitácora.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="descarte-motivo" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo del descarte..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDescartar(null)}>Cancelar</Button>
            <Button data-testid="descarte-confirmar" disabled={motivo.trim().length < 4 || busy}
              onClick={confirmarDescarte} className="bg-red-600 hover:bg-red-700">Descartar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={capturar} onOpenChange={setCapturar}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Capturar lead</DialogTitle>
            <DialogDescription>Datos mínimos del prospecto. Entrará a la bandeja como "nuevo".</DialogDescription>
          </DialogHeader>
          <LeadForm onSaved={() => { setCapturar(false); cargar(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
